import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../_services/notification.service';
import { ConfirmDeleteDialogComponent } from '../gestion-usuarios/dialogs/confirm-delete-dialog.component';
import { GestionPlantillasService } from './gestion-plantillas.service';
import { PlantillaCampo, PlantillaPayload, PlantillaResumen } from './plantilla.model';

@Component({
  selector: 'app-gestion-plantillas',
  templateUrl: './gestion-plantillas.component.html',
  styleUrls: ['./gestion-plantillas.component.scss']
})
export class GestionPlantillasComponent implements OnInit {

  plantillas: PlantillaResumen[] = [];
  campos: string[] = [];
  loading = false;

  /** Plantilla en edición (null = no hay editor abierto). */
  editing: PlantillaPayload | null = null;

  constructor(
    private service: GestionPlantillasService,
    private sanitizer: DomSanitizer,
    private notif: NotificationService,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.service.campos().subscribe({ next: c => this.campos = c || [], error: () => {} });
  }

  cargar(): void {
    this.loading = true;
    this.service.list().subscribe({
      next: data => { this.plantillas = data || []; this.loading = false; },
      error: () => {
        this.notif.error(
          this.translate.instant('COMMON.ERROR'),
          this.translate.instant('PLANTILLAS.MSG.LOAD_FAIL'),
          3000
        );
        this.loading = false;
      }
    });
  }

  nuevo(): void {
    this.editing = { nombre: '', descripcion: '', activo: true, html: this.htmlPorDefecto(), campos: [] };
  }

  editar(p: PlantillaResumen): void {
    this.service.get(p.id).subscribe({
      next: d => {
        this.editing = {
          id: d.id, nombre: d.nombre, descripcion: d.descripcion || '',
          activo: d.activo, html: d.html || '', campos: d.campos || []
        };
      },
      error: () => this.notif.error(
        this.translate.instant('COMMON.ERROR'),
        this.translate.instant('PLANTILLAS.MSG.OPEN_FAIL'),
        3000
      )
    });
  }

  // ---------- Campos dinámicos ----------
  addCampo(): void {
    if (!this.editing) return;
    if (!this.editing.campos) this.editing.campos = [];
    this.editing.campos.push({ clave: '', etiqueta: '', columnaExcel: '', obligatorio: false });
  }

  removeCampo(i: number): void {
    this.editing?.campos?.splice(i, 1);
  }

  insertarCampoDinamico(c: PlantillaCampo): void {
    if (!this.editing || !c.clave) return;
    this.editing.html = (this.editing.html || '') + `{{campo.${c.clave}}}`;
  }

  cancelar(): void { this.editing = null; }

  guardar(): void {
    if (!this.editing) return;
    if (!this.editing.nombre.trim() || !this.editing.html.trim()) {
      this.notif.warning(
        this.translate.instant('COMMON.WARNING'),
        this.translate.instant('PLANTILLAS.MSG.REQUIRED'),
        3000
      );
      return;
    }
    this.service.save(this.editing).subscribe({
      next: () => {
        this.notif.success(
          this.translate.instant('COMMON.DONE'),
          this.translate.instant('PLANTILLAS.MSG.SAVED'),
          2800
        );
        this.editing = null;
        this.cargar();
      },
      error: (e) => this.notif.error(
        this.translate.instant('COMMON.ERROR'),
        e?.error?.message || this.translate.instant('PLANTILLAS.MSG.SAVE_FAIL'),
        3500
      )
    });
  }

  eliminar(p: PlantillaResumen): void {
    const ref = this.dialog.open(ConfirmDeleteDialogComponent, {
      data: {
        title: this.translate.instant('PLANTILLAS.MSG.DELETE_TITLE'),
        message: this.translate.instant('PLANTILLAS.MSG.DELETE_MSG', { nombre: p.nombre }),
        confirmLabel: this.translate.instant('COMMON.DELETE')
      },
      panelClass: 'gu-dialog-panel'
    });
    ref.afterClosed().subscribe((ok) => {
      if (!ok) return;
      this.service.delete(p.id).subscribe({
        next: () => {
          this.notif.success(
            this.translate.instant('COMMON.DONE'),
            this.translate.instant('PLANTILLAS.MSG.DELETED'),
            2800
          );
          this.cargar();
        },
        error: (e) => this.notif.error(
          this.translate.instant('COMMON.ERROR'),
          e?.error?.message || this.translate.instant('PLANTILLAS.MSG.DELETE_FAIL'),
          3500
        )
      });
    });
  }

  insertarCampo(campo: string): void {
    if (!this.editing) return;
    this.editing.html = (this.editing.html || '') + `{{${campo}}}`;
  }

  /** Vista previa con los placeholders resaltados (no se sustituyen datos). */
  get preview(): SafeHtml {
    const html = this.editing?.html || '';
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private htmlPorDefecto(): string {
    return [
      '<?xml version="1.0" encoding="UTF-8"?>',
      '<html xmlns="http://www.w3.org/1999/xhtml">',
      '<head><meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />',
      '<style type="text/css">body{font-family:Helvetica,Arial,sans-serif;font-size:11pt;}</style>',
      '</head>',
      '<body>',
      '  <h1>{{concepto}}</h1>',
      '  <p>Destinatario: {{titularNombre}} ({{titularNif}})</p>',
      '  <div style="white-space:pre-wrap">{{descripcion}}</div>',
      '  {{#if procedimiento}}<p>Procedimiento: {{procedimiento}}</p>{{/if}}',
      '  <p>{{remitenteNombre}}</p>',
      '</body>',
      '</html>'
    ].join('\n');
  }
}
